import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Icons } from '../Icons';

const TOAST_ICON_MAP = {
    success: 'success_status',
    warning: 'warning_status',
    error: 'error_status',
    info: 'info_status',
};

/**
 * Toast — Individual toast notification component.
 *
 * Features:
 * - Auto-dismiss after `duration` ms (default 5000, use Infinity to persist)
 * - Timer pauses on hover and resumes on mouse leave
 * - Slide-out exit animation (300ms) before removal
 * - Icon changes per type (success/error/warning/info)
 *
 * Not used directly — rendered by ToastContainer, which gets its data from ToastProvider.
 */
export const Toast = ({ 
    id, 
    type = 'info', 
    title, 
    message, 
    duration = 5000, 
    onClose 
}) => {
    const [isExiting, setIsExiting] = useState(false);
    const timerRef = useRef(null);             // Active setTimeout reference
    const startTimeRef = useRef(null);         // When the current timer segment started
    const remainingTimeRef = useRef(duration); // How much time is left before auto-dismiss

    /** Start (or restart) the auto-dismiss countdown */
    const startTimer = useCallback(() => {
        if (duration === Infinity) return; // Persistent toasts never auto-dismiss
        
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(() => {
            handleClose();
        }, remainingTimeRef.current);
    }, [duration]);

    /** Pause the countdown — calculates elapsed time and saves the remainder */
    const pauseTimer = useCallback(() => {
        if (duration === Infinity) return;
        
        clearTimeout(timerRef.current);
        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current -= elapsed;
    }, [duration]);

    /** Trigger the exit animation, then remove after 300ms (matches CSS transition) */
    const exitTimerRef = useRef(null);
    const handleClose = useCallback(() => {
        setIsExiting(true);
        exitTimerRef.current = setTimeout(() => {
            onClose(id);
        }, 300);
    }, [id, onClose]);

    // Clean up exit timer on unmount
    useEffect(() => {
        return () => clearTimeout(exitTimerRef.current);
    }, []);

    // Start auto-dismiss timer on mount, clean up on unmount
    useEffect(() => {
        startTimer();
        return () => {
            clearTimeout(timerRef.current);
        };
    }, [startTimer]);


    return (
        <div 
            className={`sc-toast sc-toast-${type} ${isExiting ? 'sc-toast-exiting' : ''}`}
            onMouseEnter={pauseTimer}
            onMouseLeave={startTimer}
            role="alert"
        >
            {/* Status icon (left side) */}
            <div className="sc-toast-icon">
                <Icons name={TOAST_ICON_MAP[type] || 'info_status'} size={24} />
            </div>
            
            {/* Text content (center) */}
            <div className="sc-toast-content">
                {title && <h4 className="sc-toast-title body-m-bold">{title}</h4>}
                {message && <div className="sc-toast-message body-m">{message}</div>}
            </div>

            {/* Manual dismiss button (right side) */}
            <div className="sc-toast-close-wrapper">
                <button 
                    className="sc-toast-close" 
                    onClick={handleClose}
                    aria-label="Close notification"
                >
                    <Icons name="close" size={24} />
                </button>
            </div>
        </div>
    );
};
